import { ManagementApi } from "./api";

export const EDGE_ORIGIN = "https://pilot.dev.fivecrownsystem.com";
export const PIN_ITERATIONS = 210_000;
export interface PublicKeyRequest {
  schema_version: "fivecrown-pos-enrollment-request-v1";
  terminal_public_jwk: JsonWebKey;
  terminal_key_fingerprint: string;
}
export interface CloudTrust { key_version: number; public_jwk: JsonWebKey }
export interface CashierDraft { cashier_id: string; role: "cashier" | "supervisor"; pin: string; confirm: string }
export interface ProtectedCashier { cashier_id: string; role: "cashier" | "supervisor";
  pin: { salt_b64: string; iterations: number; hash_b64: string } }
export interface EnrollmentDraft {
  sequence: number; claim_base_url: string; valid_from: string; valid_until: string;
  cashiers: CashierDraft[]; client_certificate_chain_pem: string; server_ca_pem: string;
}
export interface PreparedEnrollment {
  idempotencyKey: string; body: Omit<EnrollmentDraft, "cashiers" | "client_certificate_chain_pem" | "server_ca_pem"> & {
    cashiers: ProtectedCashier[];
    network: { edge_base_url: string; client_certificate_chain_pem: string; server_ca_pem: string };
  };
}
export interface PublishedPackage {
  hall_id: string; session_revision_id: string; package_sha256: string; published_at: string; expires_at: string;
  inventory_leases: { terminal_id: string; lease_id: string; issued_at: string; expires_at: string; offline_safety_deadline: string }[];
  [key: string]: unknown;
}
export function safeIdentifier(value: string): string {
  if (!/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/.test(value)) throw new Error("Use a stable ID with letters, numbers, dots, colons, underscores or hyphens.");
  return value;
}
function bytesBase64(bytes: Uint8Array): string { return btoa(String.fromCharCode(...bytes)); }
function base64Bytes(value: string): Uint8Array<ArrayBuffer> {
  return Uint8Array.from(atob(value), item => item.charCodeAt(0));
}
function hex(bytes: ArrayBuffer): string { return Array.from(new Uint8Array(bytes), byte => byte.toString(16).padStart(2, "0")).join(""); }
async function publicKey(jwk: JsonWebKey): Promise<CryptoKey> {
  if (jwk.kty !== "EC" || jwk.crv !== "P-256" || typeof jwk.x !== "string" || typeof jwk.y !== "string" || "d" in jwk) {
    throw new Error("Only a public P-256 key is accepted. Never import a private key.");
  }
  return crypto.subtle.importKey("jwk", { kty: "EC", crv: "P-256", x: jwk.x, y: jwk.y }, { name: "ECDSA", namedCurve: "P-256" }, true, ["verify"]);
}
export async function spkiFingerprint(jwk: JsonWebKey): Promise<string> {
  return hex(await crypto.subtle.digest("SHA-256", await crypto.subtle.exportKey("spki", await publicKey(jwk))));
}
export async function parseKeyRequest(raw: string): Promise<PublicKeyRequest> {
  if (raw.length > 16_384) throw new Error("Terminal key request is too large.");
  const value = JSON.parse(raw) as PublicKeyRequest;
  if (!value || Array.isArray(value) || Object.keys(value).sort().join(",") !== "schema_version,terminal_key_fingerprint,terminal_public_jwk" || value.schema_version !== "fivecrown-pos-enrollment-request-v1") {
    throw new Error("Select the public enrollment request exported by the Five Crown terminal.");
  }
  const fingerprint = await spkiFingerprint(value.terminal_public_jwk);
  if (Object.keys(value.terminal_public_jwk).sort().join(",") !== "crv,kty,x,y") throw new Error("The Android request must contain only the four public P-256 key fields.");
  if (value.terminal_key_fingerprint !== fingerprint) throw new Error("The terminal key fingerprint does not match its public key.");
  return value;
}

export function certificates(pem: string): Uint8Array<ArrayBuffer>[] {
  if (pem.length > 16_000 || pem.includes("PRIVATE KEY")) throw new Error("Only public PEM certificates are accepted, up to 16 KB. Never upload a private key.");
  const pattern = /-----BEGIN CERTIFICATE-----\s*([A-Za-z0-9+/=\s]+?)\s*-----END CERTIFICATE-----/g;
  const matches = [...pem.matchAll(pattern)];
  if (matches.length < 1 || matches.length > 5 || pem.replace(pattern, "").trim()) throw new Error("Select a PEM chain containing one to five public certificates.");
  return matches.map(match => {
    const encoded = match[1].replace(/\s/g, "");
    const bytes = base64Bytes(encoded);
    if (bytesBase64(bytes) !== encoded || bytes.length < 100) throw new Error("Certificate encoding is invalid.");
    return bytes;
  });
}
interface DerNode { tag: number; start: number; content: number; end: number }
function derNode(data: Uint8Array, start: number): DerNode {
  if (start + 2 > data.length) throw new Error("Truncated certificate.");
  let length = data[start + 1]; let content = start + 2;
  if (length & 128) {
    const count = length & 127;
    if (!count || count > 3 || content + count > data.length || data[content] === 0) throw new Error("Invalid DER certificate length.");
    length = 0;
    for (let i = 0; i < count; i++) length = length * 256 + data[content++];
    if (length < 128) throw new Error("Noncanonical certificate length.");
  }
  if (content + length > data.length) throw new Error("Truncated certificate.");
  return { tag: data[start], start, content, end: content + length };
}
function children(data: Uint8Array, parent: DerNode): DerNode[] {
  if (parent.tag !== 48) throw new Error("Expected a certificate sequence.");
  const nodes: DerNode[] = []; let offset = parent.content;
  while (offset < parent.end) {
    const node = derNode(data, offset);
    if (node.end > parent.end || nodes.length > 32) throw new Error("Invalid certificate structure.");
    nodes.push(node); offset = node.end;
  }
  return nodes;
}
export async function certificateBinding(pem: string, request: PublicKeyRequest): Promise<string> {
  const leaf = certificates(pem)[0];
  const root = derNode(leaf, 0);
  if (root.end !== leaf.length) throw new Error("Trailing certificate data is not allowed.");
  const certificate = children(leaf, root);
  if (certificate.length !== 3) throw new Error("Invalid X.509 certificate.");
  const tbs = children(leaf, certificate[0]);
  const spki = tbs[tbs[0]?.tag === 160 ? 6 : 5];
  if (!spki || spki.tag !== 48) throw new Error("Certificate public key is missing.");
  const encoded = leaf.slice(spki.start, spki.end);
  await crypto.subtle.importKey("spki", encoded, { name: "ECDSA", namedCurve: "P-256" }, false, ["verify"]);
  if (hex(await crypto.subtle.digest("SHA-256", encoded)) !== request.terminal_key_fingerprint) throw new Error("The TLS client certificate belongs to a different terminal key.");
  return hex(await crypto.subtle.digest("SHA-256", leaf));
}
export async function protectCashiers(drafts: CashierDraft[]): Promise<ProtectedCashier[]> {
  if (!drafts.length || drafts.length > 50 || !drafts.some(item => item.role === "supervisor")) throw new Error("Authorize at least one supervisor and no more than 50 people.");
  if (new Set(drafts.map(item => item.cashier_id)).size !== drafts.length) throw new Error("Cashier IDs must be unique.");
  for (const draft of drafts) {
    safeIdentifier(draft.cashier_id);
    if (!["cashier", "supervisor"].includes(draft.role) || !/^[0-9]{6}$/.test(draft.pin) || draft.confirm !== draft.pin) throw new Error("Each person needs a confirmed six-digit PIN and a valid role.");
  }
  return Promise.all(drafts.map(async draft => {
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const encoded = new TextEncoder().encode(draft.pin);
    try {
      const key = await crypto.subtle.importKey("raw", encoded, "PBKDF2", false, ["deriveBits"]);
      const bits = await crypto.subtle.deriveBits({ name: "PBKDF2", hash: "SHA-256", iterations: PIN_ITERATIONS, salt }, key, 256);
      return { cashier_id: draft.cashier_id, role: draft.role,
        pin: { salt_b64: bytesBase64(salt), iterations: PIN_ITERATIONS, hash_b64: bytesBase64(new Uint8Array(bits)) } };
    } finally { encoded.fill(0); }
  }));
}
export function utc(value: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) throw new Error("Choose a valid date and time.");
  return date.toISOString();
}
export async function prepareEnrollment(draft: EnrollmentDraft, keyRequest: PublicKeyRequest): Promise<PreparedEnrollment> {
  return prepareProtectedEnrollment(draft, keyRequest, await protectCashiers(draft.cashiers));
}
export async function prepareProtectedEnrollment(draft: Omit<EnrollmentDraft, "cashiers">, keyRequest: PublicKeyRequest, cashiers: ProtectedCashier[]): Promise<PreparedEnrollment> {
  if (!cashiers.length || cashiers.length > 50 || !cashiers.some(item => item.role === "supervisor") || new Set(cashiers.map(item => item.cashier_id)).size !== cashiers.length) throw new Error("Protected staff access is required.");
  for (const person of cashiers) {
    safeIdentifier(person.cashier_id);
    if (!["cashier", "supervisor"].includes(person.role) || person.pin.iterations !== PIN_ITERATIONS || base64Bytes(person.pin.salt_b64).length !== 16 || base64Bytes(person.pin.hash_b64).length !== 32) throw new Error("Invalid protected staff access.");
  }
  if (!Number.isSafeInteger(draft.sequence) || draft.sequence < 1) throw new Error("Enrollment sequence must be a positive whole number.");
  const url = new URL(draft.claim_base_url);
  if (url.protocol !== "https:" || url.username || url.password || url.search || url.hash || url.pathname !== "/player/" || ["manage.dev.fivecrownsystem.com", "pilot.dev.fivecrownsystem.com"].includes(url.hostname)) throw new Error("Use the hall-local HTTPS URL ending in /player/, not the public cloud address.");
  const from = utc(draft.valid_from); const until = utc(draft.valid_until);
  const duration = Date.parse(until) - Date.parse(from);
  if (duration <= 0 || duration > 30 * 86400000 || Date.parse(until) <= Date.now()) throw new Error("Enrollment must have a future expiry and last no longer than 30 days.");
  await certificateBinding(draft.client_certificate_chain_pem, keyRequest);
  certificates(draft.server_ca_pem);
  return { idempotencyKey: crypto.randomUUID(), body: { sequence: draft.sequence,
    claim_base_url: url.toString(), valid_from: from, valid_until: until,
    cashiers: structuredClone(cashiers), network: { edge_base_url: EDGE_ORIGIN,
      client_certificate_chain_pem: draft.client_certificate_chain_pem, server_ca_pem: draft.server_ca_pem } } };
}
export async function registerTerminal(api: ManagementApi, hall: string, terminal: string, version: number, request: PublicKeyRequest, certificate: string): Promise<unknown> {
  if (!Number.isSafeInteger(version) || version < 1) throw new Error("Key version must be a positive whole number.");
  return api.mutate(`/v1/halls/${encodeURIComponent(safeIdentifier(hall))}/device-enrollments`, "POST", {
    hall_id: hall, device_id: safeIdentifier(terminal), device_type: "pos", status: "active",
    key_version: version, event_public_key_jwk: request.terminal_public_jwk,
    certificate_thumbprint_sha256: await certificateBinding(certificate, request),
  });
}

export function canonical(value: unknown): string {
  if (value === null || typeof value === "boolean") return JSON.stringify(value);
  if (typeof value === "string") return JSON.stringify(value).replace(/[\u007f-\uffff]/g, char => `\\u${char.charCodeAt(0).toString(16).padStart(4, "0")}`);
  if (typeof value === "number" && Number.isSafeInteger(value)) return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonical).join(",")}]`;
  if (value && typeof value === "object") {
    const object = value as Record<string, unknown>;
    const keys = Object.keys(object).sort((a, b) => {
      const aa = [...a].map(char => char.codePointAt(0)!); const bb = [...b].map(char => char.codePointAt(0)!);
      for (let i = 0; i < Math.min(aa.length, bb.length); i++) if (aa[i] !== bb[i]) return aa[i] - bb[i];
      return aa.length - bb.length;
    });
    return `{${keys.map(key => `${canonical(key)}:${canonical(object[key])}`).join(",")}}`;
  }
  throw new Error("The signed export has an unsupported value.");
}
export async function verifySignedExport(value: unknown, trust: CloudTrust, hallId: string): Promise<Record<string, unknown>> {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new Error("Signed export is not a JSON object.");
  const document = value as Record<string, unknown>; const signature = document.signature as { algorithm?: string; key_version?: number; value?: string } | undefined;
  if (document.hall_id !== hallId || !signature || signature.algorithm !== "ES256" || signature.key_version !== trust.key_version || typeof signature.value !== "string" || !/^[A-Za-z0-9_-]+$/.test(signature.value)) throw new Error("Signed export scope or cloud key version is incorrect. Refresh verified cloud trust if a reviewed rotation occurred.");
  const raw = base64Bytes(signature.value.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - signature.value.length % 4) % 4));
  const payload = Object.fromEntries(Object.entries(document).filter(([name]) => name !== "signature"));
  if (raw.length !== 64 || !await crypto.subtle.verify({ name: "ECDSA", hash: "SHA-256" }, await publicKey(trust.public_jwk), raw, new TextEncoder().encode(canonical(payload)))) throw new Error("The cloud signature on this export is invalid.");
  return document;
}
